<template>
  <div class="PlanCarousel">
    <!-- Carousel Viewport -->
    <div ref="viewportRef" class="PlanCarousel-viewport">
      <PlanMixer :plans="plans" :center="!isScrollable">
        <template #default="{ planGroup }">
          <div class="PlanCarousel-slide">
            <slot name="plan-card" :planGroup="planGroup" />
          </div>
        </template>
      </PlanMixer>
    </div>

    <PageIndicator
      v-if="showDots"
      :modelValue="selectedIndex"
      :count="totalPages"
      class="PlanCarousel-pages"
      @update:model-value="scrollTo"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import useEmblaCarousel from 'embla-carousel-vue';
import type { EmblaOptionsType } from 'embla-carousel';
import PlanMixer from '@cvo/ui/components/domain/PlanMixer/PlanMixer.vue';
import PageIndicator from '@cvo/ui/components/atoms/PageIndicator/PageIndicator.vue';
import type { Plan } from '@cvo/domain';
import { useDirection } from 'reka-ui';

defineProps<{
  plans: Plan[][];
}>();

const textDirection = useDirection();
const emblaOptions = computed<EmblaOptionsType>(() => ({
  loop: false,
  align: 'start',
  slidesToScroll: 1,
  containScroll: 'trimSnaps',
  direction: textDirection.value ?? 'ltr',
  watchDrag: (embla) => embla.scrollSnapList().length > 1,
}));
const [viewportRef, emblaApi] = useEmblaCarousel(emblaOptions);

const selectedIndex = ref(0);
const totalPages = ref(0);

const showDots = computed(() => {
  return totalPages.value > 1;
});

function scrollTo(index: number) {
  emblaApi.value?.scrollTo(index);
};

const isScrollable = computed(() => totalPages.value > 1);

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  emblaApi.value?.on('select', updateCarouselState);
  emblaApi.value?.on('reInit', updateCarouselState);

  resizeObserver = new ResizeObserver(() => {
    emblaApi.value?.reInit();
  });

  if (viewportRef.value) {
    resizeObserver.observe(viewportRef.value);
  }

  // Ensure DOM is fully updated to calculate carousel size
  nextTick(() => {
    updateCarouselState();
  });
});

function updateCarouselState() {
  if (!emblaApi.value) return;
  selectedIndex.value = emblaApi.value.selectedScrollSnap();
  totalPages.value = emblaApi.value.scrollSnapList().length;
};

onUnmounted(() => {
  resizeObserver?.disconnect();
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .PlanCarousel {
    width: 100%;

    &-viewport {
      overflow: hidden;
    }

    &-slide {
      display: flex;
      flex: 0 0 auto;
      scroll-snap-align: start;

      @include responsive.mobile {
        flex: 0 0 100%;
      }

      > * {
        flex: 1;
      }
    }

    &-pages {
      margin: vars.$dimensions-d-5 0 vars.$dimensions-d-4;
    }
  }
}
</style>
